import { useState } from 'react';
import { Search, ArrowRight, ShieldCheck, Calendar, MessageCircle } from 'lucide-react';
import { useRouter } from '../router/MarketplaceRouter';
import {
  categories,
  getFeaturedVendors,
  getPopularServices,
  getVendorById,
} from '../data/fixtures';
import {
  Button,
  PageSection,
  SectionHeader,
  ServiceCard,
  CategoryTile,
  RatingLine,
  VerifiedBadge,
} from '../shell/ui';
import { SafeImage } from '../../sor/primitives/SafeImage';

const TRUSTED_BY = [
  'Port Baku Residences',
  'Heydar Aliyev Center',
  'Fairmont Baku',
  'Crescent Beach',
  'Olive House',
];

export function HomeScreen() {
  const { navigate } = useRouter();
  const [query, setQuery] = useState('');

  const featuredCategories = categories.slice(0, 8);
  const bigCategories = categories.slice(0, 3);
  const popular = getPopularServices(6);
  const featuredVendors = getFeaturedVendors(4);

  return (
    <>
      {/* ─── HERO ────────────────────────────────────────────────────── */}
      <section
        style={{
          position: 'relative',
          background: 'var(--color-background-primary)',
          borderBottom: '1px solid var(--color-border-light)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            maxWidth: '1240px',
            margin: '0 auto',
            padding: '5rem 1.5rem 5.5rem',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)',
            gap: '4rem',
            alignItems: 'center',
          }}
          className="home-hero-grid"
        >
          {/* Left: copy + search */}
          <div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.375rem 0.875rem',
                background: 'var(--color-accent-rosegold-50)',
                border: '1px solid var(--color-accent-rosegold-200)',
                borderRadius: '999px',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'var(--color-accent-rosegold)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: '1.75rem',
              }}
            >
              A marketplace for considered work
            </div>
            <h1
              style={{
                fontFamily: 'var(--font-family-display)',
                fontSize: 'clamp(2.5rem, 5.5vw, 4.25rem)',
                fontWeight: 400,
                lineHeight: 1.05,
                letterSpacing: '-0.025em',
                color: 'var(--color-text-primary)',
                margin: 0,
                marginBottom: '1.5rem',
              }}
            >
              Find a vendor who{' '}
              <span style={{ color: 'var(--color-accent-rosegold)' }}>
                actually cares
              </span>{' '}
              about your project.
            </h1>
            <p
              style={{
                fontSize: '1.1875rem',
                lineHeight: 1.6,
                color: 'var(--color-text-secondary)',
                maxWidth: '560px',
                margin: 0,
                marginBottom: '2.5rem',
              }}
            >
              Verified Baku studios and trades for renovations, interior design, electrical, carpentry and more. Honest quotes, weekly updates, and a shortlist worth trusting.
            </p>

            {/* Search bar */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                navigate('services');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'var(--color-background-card)',
                border: '1px solid var(--color-border-medium)',
                borderRadius: 'var(--radius-lg)',
                padding: '0.375rem 0.375rem 0.375rem 1.25rem',
                boxShadow: 'var(--shadow-soft)',
                maxWidth: '560px',
              }}
            >
              <Search size={18} strokeWidth={1.75} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Try ‘full kitchen renovation, 90 m²’"
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontFamily: 'var(--font-family-body)',
                  fontSize: '1rem',
                  color: 'var(--color-text-primary)',
                  minWidth: 0,
                }}
              />
              <Button type="submit" size="md" rightIcon={<ArrowRight size={16} />}>
                Search
              </Button>
            </form>

            {/* Quick chips */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem',
                marginTop: '1.25rem',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginRight: '0.5rem' }}>
                Popular:
              </span>
              {['Kitchen renovation', 'Electrical rewire', 'Interior design', 'Lime wash', 'Smart home'].map(
                (chip) => (
                  <button
                    key={chip}
                    onClick={() => navigate('services')}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      padding: '0.3125rem 0.75rem',
                      background: 'var(--color-background-surface)',
                      border: '1px solid var(--color-border-light)',
                      borderRadius: '999px',
                      fontSize: '0.8125rem',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {chip}
                  </button>
                ),
              )}
            </div>

            {/* Trust strip — budget-inclusivity proof */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem',
                marginTop: '1.5rem',
                alignItems: 'center',
              }}
            >
              {['Avg. project: ₼3,200', '70+ trusted suppliers', '4.7★ from 1,200+ reviews'].map(
                (stat) => (
                  <span
                    key={stat}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '0.375rem 0.75rem',
                      background: 'var(--color-background-card)',
                      border: '1px solid var(--color-border-light)',
                      borderRadius: '999px',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: 'var(--color-text-secondary)',
                      fontFamily: 'var(--font-family-body)',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        background: 'var(--color-accent-rosegold)',
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                    />
                    {stat}
                  </span>
                ),
              )}
            </div>
          </div>

          {/* Right: editorial collage */}
          <div
            style={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: '1.2fr 1fr',
              gridTemplateRows: '1fr 1fr',
              gap: '1rem',
              height: '520px',
            }}
            className="home-hero-collage"
          >
            <div style={{ gridRow: '1 / span 2', borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--color-background-surface)' }}>
              <SafeImage
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=900&q=80"
                alt="Renovated apartment, Yasamal"
                fallbackHint="Renovated apartment, Yasamal"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
            <div style={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--color-background-surface)' }}>
              <SafeImage
                src="https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=700&q=80"
                alt="Interior, Sabail"
                fallbackHint="Interior, Sabail"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
            <div style={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--color-background-surface)' }}>
              <SafeImage
                src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=700&q=80"
                alt="Joinery workshop"
                fallbackHint="Joinery workshop"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>

            {/* Floating vendor card */}
            <div
              style={{
                position: 'absolute',
                left: '-1.25rem',
                bottom: '2rem',
                background: 'var(--color-background-card)',
                padding: '0.875rem 1.125rem',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-large)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                maxWidth: '260px',
              }}
            >
              <div
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '999px',
                  background:
                    'url(https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&q=80) center/cover no-repeat',
                }}
              />
              <div>
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                  Atelier Nord
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                  142 projects · ★ 4.9
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trusted-by strip */}
        <div
          style={{
            borderTop: '1px solid var(--color-border-light)',
            background: 'var(--color-background-surface)',
          }}
        >
          <div
            style={{
              maxWidth: '1240px',
              margin: '0 auto',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '2.5rem',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                color: 'var(--color-text-muted)',
                fontWeight: 500,
              }}
            >
              Trusted by teams at
            </span>
            {TRUSTED_BY.map((t) => (
              <span
                key={t}
                style={{
                  fontFamily: 'var(--font-family-display)',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: 'var(--color-text-secondary)',
                  letterSpacing: '0.02em',
                  fontStyle: 'italic',
                }}
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        <style>{`
          @media (max-width: 980px) {
            .home-hero-grid {
              grid-template-columns: 1fr !important;
              gap: 3rem !important;
            }
            .home-hero-collage {
              height: 420px !important;
            }
          }
          @media (max-width: 560px) {
            .home-hero-collage {
              height: 320px !important;
            }
          }
        `}</style>
      </section>

      {/* ─── BIG 3 CATEGORIES (editorial) ─────────────────────────────── */}
      <PageSection paddingY="4.5rem">
        <SectionHeader
          eyebrow="Categories"
          title={
            <>
              Work worth <span style={{ color: 'var(--color-accent-rosegold)' }}>doing well</span>.
            </>
          }
          description="Three editorial picks this season. Every category is vetted, and the vendors you see are actively taking bookings."
          action={
            <Button variant="secondary" onClick={() => navigate('browse')} rightIcon={<ArrowRight size={15} />}>
              View all
            </Button>
          }
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1.25rem',
          }}
          className="big-cat-grid"
        >
          {bigCategories.map((c) => (
            <CategoryTile
              key={c.id}
              name={c.name}
              tagline={c.tagline}
              image={c.image}
              serviceCount={c.serviceCount}
              onClick={() => navigate('category', { categoryId: c.id })}
              size="lg"
            />
          ))}
        </div>
        <style>{`
          @media (max-width: 820px) {
            .big-cat-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </PageSection>

      {/* ─── SMALL CATEGORY GRID ─────────────────────────────────────── */}
      <PageSection paddingY="3rem" background="var(--color-background-surface)">
        <SectionHeader
          eyebrow="Explore"
          title="Browse every trade we cover."
          description="From a single leak to an entire villa. Vendors are organised by specialty and capacity."
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1rem',
          }}
          className="small-cat-grid"
        >
          {featuredCategories.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate('category', { categoryId: c.id })}
              style={{
                all: 'unset',
                cursor: 'pointer',
                background: 'var(--color-background-card)',
                border: '1px solid var(--color-border-light)',
                borderRadius: 'var(--radius-lg)',
                padding: '1.25rem 1.25rem 1.375rem',
                transition: 'transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
              }}
              className="small-cat-card"
            >
              <div
                style={{
                  width: '100%',
                  aspectRatio: '3/2',
                  borderRadius: 'var(--radius-md)',
                  background: `url(${c.image}) center/cover no-repeat`,
                  marginBottom: '1rem',
                }}
              />
              <div
                style={{
                  fontFamily: 'var(--font-family-display)',
                  fontSize: '1.125rem',
                  fontWeight: 400,
                  color: 'var(--color-text-primary)',
                  marginBottom: '0.25rem',
                  letterSpacing: '-0.005em',
                }}
              >
                {c.name}
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                {c.serviceCount} services
              </div>
            </button>
          ))}
        </div>
        <style>{`
          .small-cat-card:hover {
            transform: translateY(-2px);
            border-color: var(--color-border-medium) !important;
            box-shadow: var(--shadow-soft);
          }
          @media (max-width: 980px) {
            .small-cat-grid { grid-template-columns: repeat(3, 1fr) !important; }
          }
          @media (max-width: 720px) {
            .small-cat-grid { grid-template-columns: repeat(2, 1fr) !important; }
          }
        `}</style>
      </PageSection>

      {/* ─── POPULAR SERVICES ────────────────────────────────────────── */}
      <PageSection>
        <SectionHeader
          eyebrow="Popular this month"
          title="Services people are booking now."
          action={
            <Button variant="secondary" onClick={() => navigate('services')} rightIcon={<ArrowRight size={15} />}>
              See all services
            </Button>
          }
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1.25rem',
          }}
          className="popular-grid"
        >
          {popular.map((s) => {
            const vendor = getVendorById(s.vendorId)!;
            return (
              <ServiceCard
                key={s.id}
                image={s.heroImage}
                title={s.title}
                vendorName={vendor.name}
                vendorAvatar={vendor.avatar}
                rating={s.rating}
                reviewCount={s.reviewCount}
                priceFrom={s.priceFrom}
                onClick={() => navigate('service', { serviceId: s.id })}
              />
            );
          })}
        </div>
        <style>{`
          @media (max-width: 980px) { .popular-grid { grid-template-columns: repeat(2, 1fr) !important; } }
          @media (max-width: 620px) { .popular-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </PageSection>

      {/* ─── HOW IT WORKS ────────────────────────────────────────────── */}
      <PageSection background="var(--color-background-surface)">
        <SectionHeader
          eyebrow="How it works"
          title={
            <>
              Three steps. <span style={{ color: 'var(--color-accent-rosegold)' }}>No noise.</span>
            </>
          }
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1.25rem',
          }}
          className="howit-grid"
        >
          {[
            {
              step: '01',
              title: 'Post a request',
              body:
                'Describe your project in plain language. Add photos if you have them, skip them if you don’t.',
              icon: <MessageCircle size={20} strokeWidth={1.5} />,
            },
            {
              step: '02',
              title: 'Compare vetted offers',
              body:
                'You get a shortlist of vendors with fixed-price offers, portfolio links, and reply times.',
              icon: <ShieldCheck size={20} strokeWidth={1.5} />,
            },
            {
              step: '03',
              title: 'Book and track work',
              body:
                'Weekly photo updates, a single messaging thread, and a holdback until you’re happy.',
              icon: <Calendar size={20} strokeWidth={1.5} />,
            },
          ].map((s) => (
            <div
              key={s.step}
              style={{
                background: 'var(--color-background-card)',
                border: '1px solid var(--color-border-light)',
                borderRadius: 'var(--radius-lg)',
                padding: '2rem 1.75rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1.25rem',
                }}
              >
                <div
                  style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--color-accent-rosegold-50)',
                    color: 'var(--color-accent-rosegold)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {s.icon}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-family-display)',
                    fontStyle: 'italic',
                    fontSize: '0.875rem',
                    color: 'var(--color-text-muted)',
                    letterSpacing: '0.05em',
                  }}
                >
                  {s.step}
                </div>
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-family-display)',
                  fontSize: '1.375rem',
                  fontWeight: 400,
                  color: 'var(--color-text-primary)',
                  letterSpacing: '-0.005em',
                  marginBottom: '0.625rem',
                }}
              >
                {s.title}
              </div>
              <p style={{ fontSize: '0.9375rem', lineHeight: 1.55, color: 'var(--color-text-secondary)', margin: 0 }}>
                {s.body}
              </p>
            </div>
          ))}
        </div>
        <style>{`
          @media (max-width: 900px) {
            .howit-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </PageSection>

      {/* ─── FEATURED VENDORS ────────────────────────────────────────── */}
      <PageSection>
        <SectionHeader
          eyebrow="Featured vendors"
          title="Studios and trades worth knowing."
          description="A small, hand-picked group of Baku vendors. Every profile is vetted, every rating comes from a verified project."
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1.25rem',
          }}
          className="vendor-grid"
        >
          {featuredVendors.map((v) => (
            <button
              key={v.id}
              onClick={() => navigate('vendor', { vendorId: v.id })}
              style={{
                all: 'unset',
                cursor: 'pointer',
                display: 'block',
                background: 'var(--color-background-card)',
                border: '1px solid var(--color-border-light)',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                transition: 'transform 250ms ease, box-shadow 250ms ease, border-color 250ms ease',
              }}
              className="vendor-card"
            >
              <div
                style={{
                  aspectRatio: '4/3',
                  background: `url(${v.cover}) center/cover no-repeat`,
                }}
              />
              <div style={{ padding: '1.125rem 1.25rem 1.375rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.625rem' }}>
                  <div
                    style={{
                      fontFamily: 'var(--font-family-display)',
                      fontSize: '1.125rem',
                      fontWeight: 400,
                      color: 'var(--color-text-primary)',
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {v.name}
                  </div>
                  {v.verified && <VerifiedBadge />}
                </div>
                <div
                  style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                    marginBottom: '0.875rem',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {v.headline}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.8125rem',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  <RatingLine rating={v.rating} reviewCount={v.reviewCount} />
                  <span>{v.completedProjects} projects</span>
                </div>
              </div>
            </button>
          ))}
        </div>
        <style>{`
          .vendor-card:hover {
            transform: translateY(-3px);
            border-color: var(--color-border-medium) !important;
            box-shadow: var(--shadow-medium);
          }
          @media (max-width: 1100px) { .vendor-grid { grid-template-columns: repeat(2, 1fr) !important; } }
          @media (max-width: 620px) { .vendor-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </PageSection>

      {/* ─── CTA BAND ───────────────────────────────────────────────── */}
      <PageSection paddingY="4.5rem">
        <div
          style={{
            position: 'relative',
            borderRadius: 'var(--radius-2xl)',
            overflow: 'hidden',
            padding: '4rem 3.5rem',
            background:
              'linear-gradient(120deg, rgba(28, 27, 26, 0.86) 0%, rgba(28, 27, 26, 0.74) 55%, rgba(45, 106, 79, 0.72) 100%), url(https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?auto=format&fit=crop&w=1600&q=80) center/cover no-repeat',
            color: 'var(--color-text-inverse)',
          }}
          className="home-cta"
        >
          <div style={{ maxWidth: '640px' }}>
            <div
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                opacity: 0.75,
                marginBottom: '1rem',
              }}
            >
              Start today
            </div>
            <h3
              style={{
                fontFamily: 'var(--font-family-display)',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 400,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                margin: 0,
                marginBottom: '1.25rem',
              }}
            >
              Have a project in mind? Post it in{' '}
              <span style={{ color: 'var(--color-accent-rosegold)' }}>two minutes</span>.
            </h3>
            <p
              style={{
                fontSize: '1.0625rem',
                lineHeight: 1.6,
                opacity: 0.88,
                marginBottom: '2rem',
                maxWidth: '520px',
              }}
            >
              Describe it in plain language. We’ll match you with verified vendors and route offers back to one place. No cold calls.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Button
                onClick={() => navigate('request')}
                size="lg"
                rightIcon={<ArrowRight size={17} />}
              >
                Post a request
              </Button>
              <Button
                variant="secondary"
                size="lg"
                onClick={() => navigate('browse')}
                style={{
                  background: 'transparent',
                  color: 'var(--color-text-inverse)',
                  border: '1px solid rgba(255,255,255,0.4)',
                }}
              >
                Browse first
              </Button>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 760px) {
            .home-cta { padding: 3rem 1.75rem !important; }
          }
        `}</style>
      </PageSection>
    </>
  );
}
